import React, { useState, useEffect, useRef } from 'react';

import { Clear } from '@mui/icons-material';
import Grid from '@mui/material/Grid2';

import { 
  Character,
  CharacterAbility,
  CharacterSheetInterface,
  CharacterSpell,
  CreatureAbility,
  BasicCreatureInstance,
  MinionAbility,
  Spell,
  SummonInstance,
  SummonOption2,
  TransformInstance,
  ComplexCreatureInstance,
} from "@dndModels";

import API from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";

import CharacterMinionsButton from "@dndComponents/character/minions/CharacterMinionsButton";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import CreatureInstanceInput from "@dndComponents/creature/CreatureInstanceInput";
import CreatureInstanceInputOld from "@dndComponents/creature/CreatureInstanceInputOld";
import { SpellLevel } from '@dndModels/Types';

interface Props {
  character: Character;
  char_interface: CharacterSheetInterface;
  disabled?: boolean;
  shared: boolean;
}

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();

export default function CharacterMinions({ 
  character,
  char_interface,
  disabled,
  shared,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [open, setOpen] = useState(false);
  const [minion, setMinion] = useState<BasicCreatureInstance | SummonInstance | TransformInstance | ComplexCreatureInstance | null>(null);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const recalculated = () => {
    console.log("Minions");
    setReloading(true);
  }

  // eslint-disable-next-line
  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      character.subscription_holder.subscribe("Minions", mounted, recalculated);
    }
  });
  
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const renderMinion = (the_minion: BasicCreatureInstance | SummonInstance | TransformInstance | ComplexCreatureInstance) => {
    const enabled = api.user && (character.owner_id === api.user._id || (character.campaign && character.campaign.owner_id === api.user._id));
    const name = the_minion.name;
    console.log(the_minion);
    return (
      <span key={the_minion.true_id} style={{ 
        borderTop: "1px solid",
        width: "100%"
      }}>
        <Grid container spacing={0} direction="row" >
          <Grid size={12}>
            <div className="clickable"
              style={{ float: "left", paddingLeft: "4px" }}
              onClick={() => {
                setMinion(the_minion);
                setOpen(true);
              }}>
              { name }
            </div>
            { the_minion.image_url !== "" &&
              <>
                <img className="clickable" src={ the_minion.image_url } alt="minion image" 
                  style={{
                    maxWidth: "5em",
                    maxHeight: "5em",
                    border: "1px solid",
                  }} 
                  onClick={() => {
                    setMinion(the_minion);
                    setOpen(true);
                  }}
                />
              </>
            }
            <div style={{ float: "right" }}>
              <Clear className="clickable"
                onClick={() => {
                  if (enabled) {
                    character.minions = character.minions.filter(o => o.true_id !== the_minion.true_id);
                    character.subscription_holder.recalculate();
                    character.changed();
                    setReloading(true);
                  }
                }} 
              />
            </div>
          </Grid>
          <Grid size={12}
            container
            spacing={0}
            direction="row"
            style={{ 
              margin: "1px",
              width: "80px" 
            }}
            onClick={() => {
              setMinion(the_minion);
              setOpen(true);
            }}>
            <Grid size={5}>
              <div style={{ float: "right" }}>
                { the_minion.hp.current } 
                { the_minion.hp.temp > 0 && <span style={{ color: "blue" }}>+{the_minion.hp.temp}</span> } 
              </div>
            </Grid>
            <Grid size={7}>
              &nbsp;/&nbsp;
                { the_minion.hp.override_max === null ? 
                  the_minion.hp.max 
                : the_minion.hp.override_max > the_minion.hp.max ?
                  <span style={{ color: "green" }}>{ the_minion.hp.override_max }</span> 
                : 
                  <span style={{ color: "red" }}>{ the_minion.hp.override_max }</span> 
                } 
              { the_minion.hp.max_modifier > 0 ? <span style={{ color: "green" }}>+{the_minion.hp.max_modifier}</span> : (the_minion.hp.max_modifier < 0 && <span style={{ color: "red" }}>{the_minion.hp.max_modifier}</span>) }
            </Grid>
          </Grid>
        </Grid>
      </span>
    );
  }

  const renderExtras = () => {
    return (
      <>
        <ModalOrDrawer
          open={ open } 
          // drawer_only
          onClose={() => {
            setOpen(false);
            character.subscription_holder.modalClosed();
          }}>
          { open && renderMinionStatBlock() }
        </ModalOrDrawer>
      </>
    );
  }

  const renderMinionStatBlock = () => {
    if (minion) {
      return (
        <div style={{ 
          height: "100%",
          overflowX: "hidden"
        }}>
          { minion instanceof BasicCreatureInstance || minion instanceof SummonInstance || minion instanceof ComplexCreatureInstance ?
            <CreatureInstanceInput
              character={character}
              creature_instance={minion}
              true_id={minion.true_id}
              onChange={() => {
                character.subscription_holder.recalculate();
                character.changed();
              }}
              onSummon={(spell: CharacterAbility | CharacterSpell | Spell | CreatureAbility | MinionAbility, level: SpellLevel, selected_option: SummonOption2) => {
                character.changed();
                char_util.summonMinions({ character, spell, level, selected_option }).then(() => {
                  character.subscription_holder.recalculate();
                  setReloading(true);
                });
              }}
            />
          :
            <CreatureInstanceInputOld
              character={character}
              creature_instance={minion}
              true_id={minion.true_id}
              onChange={() => {
                character.subscription_holder.recalculate();
                character.changed();
              }}
              onSummon={(spell: CharacterAbility | CharacterSpell | Spell | CreatureAbility | MinionAbility, level: SpellLevel, selected_option: SummonOption2) => {
                character.changed();
                char_util.summonMinions({ character, spell, level, selected_option }).then(() => {
                  character.subscription_holder.recalculate();
                  setReloading(true);
                });
              }}
            />
          }
        </div>
      );
    }
    return null;
  }

  return (
    <Grid className="curved_border bg_black_transp">
      <div>
        <Grid container spacing={0} direction="row">
          <Grid size={12}>
            <CharacterMinionsButton
              character={character}
              char_interface={char_interface}
              disabled={disabled || shared}
              onChange={() => {
                character.subscription_holder.recalculate();
                setReloading(true);
              }}
            />
          </Grid>
          { character.minions.sort((a,b) => {return a.name.localeCompare(b.name)}).map(minion => {
            return renderMinion(minion);
          })}
        </Grid>
      </div>
      { renderExtras() }
    </Grid>
  );
}
